import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Briefcase, Clock, CheckCircle, LogOut, Key, Search, Filter } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAutoLogout } from '../hooks/useAutoLogout';
import AceptarTrabajoModal from '../components/AceptarTrabajoModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClienteDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  useAutoLogout(); // Enable auto-logout after 5 minutes of inactivity
  const [trabajos, setTrabajos] = useState([]);
  const [filteredTrabajos, setFilteredTrabajos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('todos');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTrabajo, setSelectedTrabajo] = useState(null);

  useEffect(() => {
    loadTrabajos();
  }, []);

  useEffect(() => {
    filterTrabajos();
  }, [trabajos, searchTerm, estadoFilter]);

  const loadTrabajos = async () => {
    try {
      setLoading(true);
      // Filtrar trabajos por cliente_id (empresa del usuario)
      const response = await axios.get(`${API}/trabajos`, {
        params: { cliente_id: user.cliente_id }
      });
      setTrabajos(response.data);
    } catch (err) {
      setError('Error al cargar trabajos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filterTrabajos = () => {
    let filtered = [...trabajos];

    // Filtrar por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.descripcion.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.tecnico_asignado_nombre && t.tecnico_asignado_nombre.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filtrar por estado
    if (estadoFilter !== 'todos') {
      filtered = filtered.filter(t => t.estado === estadoFilter);
    }

    setFilteredTrabajos(filtered);
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'en_progreso':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'completado':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'aceptado':
        return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'pendiente':
        return <Clock className="h-4 w-4" />;
      case 'en_progreso':
        return <Briefcase className="h-4 w-4" />;
      case 'completado':
        return <CheckCircle className="h-4 w-4" />;
      case 'aceptado':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const formatEstado = (estado) => {
    const estados = {
      pendiente: 'Pendiente',
      en_progreso: 'En Progreso',
      completado: 'Completado',
      aceptado: 'Aceptado'
    };
    return estados[estado] || estado;
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleAceptarClick = (e, trabajo) => {
    e.stopPropagation(); // Evitar que se active el onClick del row
    setSelectedTrabajo(trabajo);
    setModalOpen(true);
  };

  const handleAceptarSuccess = () => {
    loadTrabajos(); // Recargar la lista
    setModalOpen(false);
    setSelectedTrabajo(null);
  };

  const stats = {
    pendientes: trabajos.filter(t => t.estado === 'pendiente').length,
    en_progreso: trabajos.filter(t => t.estado === 'en_progreso').length,
    completados: trabajos.filter(t => t.estado === 'completado').length,
    aceptados: trabajos.filter(t => t.estado === 'aceptado').length
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 p-4 md:p-6" data-testid="cliente-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <img 
                src="/surnexa-logo.png" 
                alt="Surnexa Logo" 
                className="w-32 h-auto object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900" data-testid="dashboard-title">
                  Panel de Cliente
                </h1>
                <p className="text-gray-600 mt-1">Bienvenido, {user?.nombre_completo}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/cambiar-contrasena')}
                data-testid="change-password-button"
              >
                <Key className="mr-2 h-4 w-4" />
                Cambiar Clave
              </Button>
              <Button variant="outline" onClick={logout} data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" />
                Salir
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="bg-yellow-50 border-yellow-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-yellow-800">Pendientes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-900">{stats.pendientes}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-blue-800">En Progreso</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-900">{stats.en_progreso}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-green-50 border-green-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-green-800">Completados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-900">{stats.completados}</div>
            </CardContent>
          </Card>

          <Card className="bg-emerald-50 border-emerald-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-emerald-800">Aceptados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-900">{stats.aceptados}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por título, descripción o técnico..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <div className="w-full sm:w-48">
                <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                  <SelectTrigger data-testid="filter-select">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los estados</SelectItem>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                    <SelectItem value="en_progreso">En Progreso</SelectItem>
                    <SelectItem value="completado">Completado</SelectItem>
                    <SelectItem value="aceptado">Aceptado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Trabajos List */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Trabajos {filteredTrabajos.length !== trabajos.length && `(${filteredTrabajos.length} de ${trabajos.length})`}
          </h2>
          
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <Card>
              <CardContent className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Cargando trabajos...</p>
              </CardContent>
            </Card>
          ) : filteredTrabajos.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Briefcase className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">
                  {searchTerm || estadoFilter !== 'todos'
                    ? 'No se encontraron trabajos con los filtros aplicados'
                    : 'No tienes trabajos registrados'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trabajo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lugar
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ciudad
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Técnico
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Finalizado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTrabajos.map((trabajo) => (
                    <tr
                      key={trabajo.id}
                      onClick={() => navigate(`/cliente/trabajo/${trabajo.id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      data-testid={`trabajo-row-${trabajo.id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="text-sm font-medium text-gray-900">{trabajo.titulo}</div>
                          <div className="text-sm text-gray-500 line-clamp-1">{trabajo.descripcion}</div>
                          {trabajo.direccion && (
                            <div className="text-xs text-gray-400 mt-1">{trabajo.direccion}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {trabajo.lugar_nombre || <span className="text-gray-400">No especificado</span>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {trabajo.ciudad_nombre || <span className="text-gray-400">No especificado</span>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {trabajo.tecnico_asignado_nombre || 'No asignado'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={`${getEstadoColor(trabajo.estado)} flex items-center gap-1 w-fit`}>
                          {getEstadoIcon(trabajo.estado)}
                          {formatEstado(trabajo.estado)}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatFecha(trabajo.fecha_creacion)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {trabajo.fecha_finalizacion ? (
                          <span className="text-green-600 font-medium">
                            {formatFecha(trabajo.fecha_finalizacion)}
                          </span>
                        ) : (
                          <span className="text-gray-400">Pendiente</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {trabajo.estado === 'completado' ? (
                          trabajo.firma_base64 ? (
                            <Button
                              size="sm"
                              onClick={(e) => handleAceptarClick(e, trabajo)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Aceptar
                            </Button>
                          ) : (
                            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                              Sin firma de técnico
                            </Badge>
                          )
                        ) : trabajo.estado === 'aceptado' ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                            ✓ Aceptado
                          </Badge>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Aceptación */}
      <AceptarTrabajoModal
        trabajo={selectedTrabajo}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handleAceptarSuccess}
      />
    </div>
  );
};

export default ClienteDashboard;